import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, Carousel, Heading, HeadingProps, Icon, IconProps, SimpleGrid, SimpleGridProps, StackProps, Text, TextProps, VStack } from '@chakra-ui/react';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import React from 'react';
import { IconType } from 'react-icons';
export interface GenericStatItem {
  /** react-icons component or any custom element rendered above the text. */
  icon: IconType | React.ReactElement;
  text: string;
  iconProps?: IconProps;
  textProps?: TextProps;
  cardProps?: StackProps;
}
export interface GenericStatsProps {
  heading: string;
  stats: GenericStatItem[];
  /** Section background. Defaults to `colorPalette.panel`. */
  background?: BoxProps['background'];
  colorPalette?: BoxProps['colorPalette'];
  /** Column count for the desktop grid. Defaults to `Math.min(stats.length, 4)`. */
  columns?: SimpleGridProps['columns'];
  containerProps?: BoxProps;
  headingProps?: HeadingProps;
  gridProps?: SimpleGridProps;
  carouselProps?: Omit<Carousel.RootProps, 'slideCount'>;
  /** Shared defaults applied to every stat card (per-item `cardProps` win). */
  cardProps?: StackProps;
  /** Shared defaults applied to every stat icon (per-item `iconProps` win). */
  iconProps?: IconProps;
  /** Shared defaults applied to every stat text (per-item `textProps` win). */
  textProps?: TextProps;
}
function renderStatIcon(icon: GenericStatItem['icon'], iconProps?: IconProps) {
  if (React.isValidElement(icon)) {
    return icon;
  }
  return <Icon as={icon} boxSize={10} color={'colorPalette.solid'} {...iconProps} />;
}
const StatCard: React.FC<{
  stat: GenericStatItem;
  cardProps?: StackProps;
  iconProps?: IconProps;
  textProps?: TextProps;
  padding?: StackProps['padding'];
}> = t0 => {
  const $ = _c(14);
  const {
    stat,
    cardProps,
    iconProps,
    textProps,
    padding
  } = t0;
  const t1 = stat.cardProps;
  let t2;
  if ($[0] !== iconProps || $[1] !== stat.icon || $[2] !== stat.iconProps) {
    t2 = renderStatIcon(stat.icon, {
      ...iconProps,
      ...stat.iconProps
    });
    $[0] = iconProps;
    $[1] = stat.icon;
    $[2] = stat.iconProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== stat.text || $[5] !== stat.textProps || $[6] !== textProps) {
    t3 = <Text color="colorPalette.fg" fontWeight="semibold" {...textProps} {...stat.textProps}>{stat.text}</Text>;
    $[4] = stat.text;
    $[5] = stat.textProps;
    $[6] = textProps;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== cardProps || $[9] !== padding || $[10] !== stat.cardProps || $[11] !== t2 || $[12] !== t3) {
    t4 = <VStack align="center" borderColor="colorPalette.border" borderRadius="2xl" borderWidth="1px" gap={4} height="full" justify="center" padding={padding} textAlign="center" width="full" {...cardProps} {...t1}>{t2}{t3}</VStack>;
    $[8] = cardProps;
    $[9] = padding;
    $[10] = stat.cardProps;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};

/**
 * GenericStats – A full-page landing block with a centered heading and a row of
 * simple icon + text stat cards. Uses a Carousel on mobile/tablet and a grid on desktop.
 */
export const GenericStats: React.FC<GenericStatsProps> = t0 => {
  const $ = _c(43);
  const {
    heading,
    stats,
    background: t1,
    colorPalette,
    columns,
    containerProps,
    headingProps,
    gridProps,
    carouselProps,
    cardProps,
    iconProps,
    textProps
  } = t0;
  const background = t1 === undefined ? "colorPalette.panel" : t1;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t2;
  if ($[0] !== getBreakpointValue) {
    t2 = getBreakpointValue([true, true, true, false, false]);
    $[0] = getBreakpointValue;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const useCarousel = t2;
  const resolvedColumns = columns ?? Math.min(stats.length);
  let t3;
  if ($[2] !== getBreakpointValue) {
    t3 = getBreakpointValue([4, 6, 8, 10]);
    $[2] = getBreakpointValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const cardPadding = t3;
  let t4;
  if ($[4] !== getBreakpointValue) {
    t4 = getBreakpointValue(["md", "lg", "xl"]);
    $[4] = getBreakpointValue;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4 || $[7] !== textProps) {
    t5 = {
      fontSize: t4,
      ...textProps
    };
    $[6] = t4;
    $[7] = textProps;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const cardTextProps = t5;
  let t6;
  if ($[9] !== getBreakpointValue) {
    t6 = getBreakpointValue([3, 4, 6, 8]);
    $[9] = getBreakpointValue;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== getBreakpointValue) {
    t7 = getBreakpointValue([8, 12, 14, 14, 20, 24]);
    $[11] = getBreakpointValue;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== getBreakpointValue) {
    t8 = getBreakpointValue([8, 8, 10, 12]);
    $[13] = getBreakpointValue;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== getBreakpointValue) {
    t9 = getBreakpointValue(["2xl", "3xl", "4xl", "4xl", "5xl"]);
    $[15] = getBreakpointValue;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== heading || $[18] !== headingProps || $[19] !== t9) {
    t10 = <Heading as="h2" size={t9} textAlign="center" {...headingProps}>{heading}</Heading>;
    $[17] = heading;
    $[18] = headingProps;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== cardPadding || $[22] !== cardProps || $[23] !== cardTextProps || $[24] !== carouselProps || $[25] !== getBreakpointValue || $[26] !== gridProps || $[27] !== iconProps || $[28] !== resolvedColumns || $[29] !== stats || $[30] !== useCarousel) {
    t11 = useCarousel ? <Carousel.Root slideCount={stats.length} width="full" {...carouselProps}><Carousel.ItemGroup>{stats.map((stat, index) => <Carousel.Item key={`${stat.text}-${index}`} index={index} paddingX={2}><StatCard cardProps={cardProps} iconProps={iconProps} padding={cardPadding} stat={stat} textProps={cardTextProps} /></Carousel.Item>)}</Carousel.ItemGroup><Carousel.Control justifyContent="center"><Carousel.Indicators /></Carousel.Control></Carousel.Root> : <SimpleGrid columns={resolvedColumns} gap={getBreakpointValue([4, 4, 6])} width="full" {...gridProps}>{stats.map((stat_0, index_0) => <StatCard key={`${stat_0.text}-${index_0}`} cardProps={cardProps} iconProps={iconProps} padding={cardPadding} stat={stat_0} textProps={cardTextProps} />)}</SimpleGrid>;
    $[21] = cardPadding;
    $[22] = cardProps;
    $[23] = cardTextProps;
    $[24] = carouselProps;
    $[25] = getBreakpointValue;
    $[26] = gridProps;
    $[27] = iconProps;
    $[28] = resolvedColumns;
    $[29] = stats;
    $[30] = useCarousel;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t8) {
    t12 = <VStack align="center" gap={t8} marginX="auto" maxWidth="container.xl" width="full">{t10}{t11}</VStack>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t8;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== background || $[37] !== colorPalette || $[38] !== containerProps || $[39] !== t12 || $[40] !== t6 || $[41] !== t7) {
    t13 = <Box as="section" background={background} colorPalette={colorPalette} paddingX={t6} paddingY={t7} width="full" {...containerProps}>{t12}</Box>;
    $[36] = background;
    $[37] = colorPalette;
    $[38] = containerProps;
    $[39] = t12;
    $[40] = t6;
    $[41] = t7;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
export default GenericStats;